import React, { useState, useEffect, useContext } from "react";
import {
  ArrowRightLeft,
  ChartCandlestick,
  Clock3,
  ImagePlus,
  Loader2,
  ShieldAlert,
  Tags,
} from "lucide-react";
import { AuthContext } from "./AuthContext";
import { API_BASE_URL } from "./api";
import {
  Button,
  Field,
  MetricCard,
  SectionHeader,
  SelectField,
  StatusPill,
  SurfaceCard,
  TextareaField,
} from "./components/designSystem";
import {
  ASSET_TYPE_OPTIONS,
  CURRENCY_OPTIONS,
  resolveTradeInstrumentInput,
} from "./utils/tradeInstrumentSpecs";

const EMPTY_FORM = {
  asset: "",
  assetType: "forex",
  tradeCurrency: "USD",
  direction: "long",
  entry_price: "",
  exit_price: "",
  lot_size: "",
  quantity: "",
  notes: "",
  opened_at: "",
  closed_at: "",
  setupDescription: "",
  lessonsLearned: "",
  marketCondition: "",
  emotionalState: "",
  executionQuality: "good",
  ruleAdherence: "followed",
  confidenceScore: "5",
  setupTagsText: "",
  mistakeTagsText: "",
};

function toDateTimeLocal(value) {
  if (!value) {
    return "";
  }

  return new Date(value).toISOString().slice(0, 16);
}

function parseTagText(value) {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export default function NewTradeForm({ onSaved, editTrade, clearEdit }) {
  const { token, user } = useContext(AuthContext);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [monthlyTradeCount, setMonthlyTradeCount] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingScreenshots, setExistingScreenshots] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const instrument = resolveTradeInstrumentInput({
    asset: form.asset,
    assetType: form.assetType,
    tradeCurrency: form.tradeCurrency,
  });
  const usesLotSize = instrument.positionField === "lot_size";
  const tradeLimitReached = user?.plan === "free" && !editTrade && monthlyTradeCount >= 30;
  const directionHelper = form.direction === "long"
    ? "Long trades expect price appreciation from entry to exit."
    : "Short trades benefit from downside movement and require tighter exit discipline.";
  const timingStatus = form.opened_at && form.closed_at && new Date(form.closed_at) < new Date(form.opened_at)
    ? "Close time is earlier than open time."
    : "";
  const instrumentHint = instrument.inferredFromSymbol
    ? `${instrument.instrumentLabel} was recognized automatically. Asset type and trade currency are locked to keep profit calculations consistent.`
    : "For custom symbols, set the asset type and settlement currency manually.";
  const positionHint = usesLotSize
    ? "Lot-based contracts use broker-style contract sizes in the backend calculator."
    : "Quantity-based contracts use share or coin units directly in the backend calculator.";

  useEffect(() => {
    async function fetchTradeCount() {
      if (!token) {
        return;
      }

      try {
        const res = await fetch(`${API_BASE_URL}/api/trades`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        const tradeList = Array.isArray(data) ? data : [];
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        const count = tradeList.filter((trade) => {
          const created = new Date(trade.created_at);
          return created >= monthStart && created <= monthEnd;
        }).length;
        setMonthlyTradeCount(count);
      } catch (fetchError) {
        setMonthlyTradeCount(0);
      }
    }

    fetchTradeCount();
  }, [token]);

  useEffect(() => {
    if (!editTrade) {
      setForm(EMPTY_FORM);
      setExistingScreenshots([]);
      setSelectedFiles([]);
      return;
    }

    setForm({
      asset: editTrade.asset || "",
      assetType: editTrade.assetType || "forex",
      tradeCurrency: editTrade.tradeCurrency || "USD",
      direction: editTrade.direction || "long",
      entry_price: editTrade.entry_price ?? "",
      exit_price: editTrade.exit_price ?? "",
      lot_size: editTrade.lot_size ?? "",
      quantity: editTrade.quantity ?? "",
      notes: editTrade.notes || "",
      opened_at: toDateTimeLocal(editTrade.opened_at),
      closed_at: toDateTimeLocal(editTrade.closed_at),
      setupDescription: editTrade.setupDescription || "",
      lessonsLearned: editTrade.lessonsLearned || "",
      marketCondition: editTrade.marketCondition || "",
      emotionalState: editTrade.emotionalState || "",
      executionQuality: editTrade.executionQuality || "good",
      ruleAdherence: editTrade.ruleAdherence || "followed",
      confidenceScore: editTrade.confidenceScore ? String(editTrade.confidenceScore) : "5",
      setupTagsText: Array.isArray(editTrade.setupTags) ? editTrade.setupTags.join(", ") : "",
      mistakeTagsText: Array.isArray(editTrade.mistakeTags) ? editTrade.mistakeTags.join(", ") : "",
    });
    setExistingScreenshots(Array.isArray(editTrade.screenshots) ? editTrade.screenshots : []);
    setSelectedFiles([]);
  }, [editTrade]);

  useEffect(() => {
    setForm((current) => {
      const next = { ...current };
      let changed = false;

      if (instrument.resolvedAssetType !== current.assetType) {
        next.assetType = instrument.resolvedAssetType;
        changed = true;
      }

      if (instrument.inferredTradeCurrency && instrument.inferredTradeCurrency !== current.tradeCurrency) {
        next.tradeCurrency = instrument.inferredTradeCurrency;
        changed = true;
      }

      if (usesLotSize && current.quantity !== "") {
        next.quantity = "";
        changed = true;
      }

      if (!usesLotSize && current.lot_size !== "") {
        next.lot_size = "";
        changed = true;
      }

      return changed ? next : current;
    });
  }, [instrument.inferredTradeCurrency, instrument.resolvedAssetType, usesLotSize]);

  const handleFileSelection = (event) => {
    setSelectedFiles(Array.from(event.target.files || []));
  };

  const removeExistingScreenshot = (urlToRemove) => {
    setExistingScreenshots((current) => current.filter((item) => item.url !== urlToRemove));
  };

  const submit = async (e) => {
    e.preventDefault();

    if (isSaving) {
      return;
    }

    setError("");
    setIsSaving(true);

    const url = editTrade ? `${API_BASE_URL}/api/trades/${editTrade._id}` : `${API_BASE_URL}/api/trades`;
    const method = editTrade ? "PUT" : "POST";
    const payload = new FormData();

    Object.entries({
      asset: form.asset,
      assetType: instrument.resolvedAssetType,
      tradeCurrency: instrument.inferredTradeCurrency,
      direction: form.direction,
      entry_price: form.entry_price,
      exit_price: form.exit_price,
      lot_size: usesLotSize ? form.lot_size : undefined,
      quantity: usesLotSize ? undefined : form.quantity,
      notes: form.notes,
      opened_at: form.opened_at,
      closed_at: form.closed_at,
      setupDescription: form.setupDescription,
      lessonsLearned: form.lessonsLearned,
      marketCondition: form.marketCondition,
      emotionalState: form.emotionalState,
      executionQuality: form.executionQuality,
      ruleAdherence: form.ruleAdherence,
      confidenceScore: form.confidenceScore,
      setupTags: JSON.stringify(parseTagText(form.setupTagsText)),
      mistakeTags: JSON.stringify(parseTagText(form.mistakeTagsText)),
      existingScreenshots: JSON.stringify(existingScreenshots),
    }).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        payload.append(key, value);
      }
    });

    selectedFiles.forEach((file) => payload.append("screenshots", file));

    try {
      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: payload,
      });

      let data = null;
      try {
        data = await res.json();
      } catch (jsonErr) {}

      if (!res.ok) {
        setError((data && data.error) || "Failed to save trade.");
        return;
      }

      setForm(EMPTY_FORM);
      setSelectedFiles([]);
      setExistingScreenshots([]);
      setError("");
      clearEdit();
      onSaved();
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <SurfaceCard className="overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.12),_transparent_22%),linear-gradient(135deg,_#ffffff,_#f8fafc)] dark:bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.12),_transparent_22%),linear-gradient(135deg,_#111827,_#0f172a)]">
        <SectionHeader
          eyebrow={editTrade ? "Trade Revision" : "Trade Entry"}
          title={editTrade ? "Refine an existing trade" : "Capture a fresh market execution"}
          description="Record setup quality, execution discipline, mistakes, and screenshots so your future review is evidence-based rather than emotional."
        />
        <div className="mt-6 grid gap-4 lg:grid-cols-4">
          <MetricCard label="Plan" value={user?.plan ? user.plan.toUpperCase() : "FREE"} helper="Current account tier." />
          <MetricCard label="This month" value={String(monthlyTradeCount)} helper={user?.plan === "free" ? "Free plan allows up to 30 trades each month." : "Trades recorded in the current month."} />
          <MetricCard label="Execution type" value={form.direction.toUpperCase()} helper={directionHelper} />
          <MetricCard label="Instrument model" value={instrument.instrumentLabel.toUpperCase()} helper={usesLotSize ? "Lot-based pricing enabled." : "Unit-based pricing enabled."} />
        </div>
      </SurfaceCard>

      {tradeLimitReached ? (
        <SurfaceCard className="border-amber-200 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-500/10">
          <div className="flex items-start gap-3">
            <ShieldAlert className="mt-1 h-5 w-5 text-amber-600 dark:text-amber-300" />
            <div>
              <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-200">Monthly limit reached for the free plan</h3>
              <p className="mt-2 text-sm leading-7 text-amber-700 dark:text-amber-300">
                Free accounts can log up to 30 trades each month. Upgrade to Pro to continue logging new trades while preserving analytics, notes, and screenshots.
              </p>
            </div>
          </div>
        </SurfaceCard>
      ) : null}

      {error ? (
        <SurfaceCard className="border-rose-200 bg-rose-50 dark:border-rose-500/20 dark:bg-rose-500/10">
          <p className="text-sm font-semibold text-rose-700 dark:text-rose-300">{error}</p>
        </SurfaceCard>
      ) : null}

      {timingStatus ? (
        <SurfaceCard className="border-amber-200 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-500/10">
          <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">{timingStatus}</p>
        </SurfaceCard>
      ) : null}

      <form onSubmit={submit}>
        <fieldset disabled={tradeLimitReached || isSaving} style={{ border: 0, padding: 0, margin: 0 }}>
          <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
            <SurfaceCard>
              <SectionHeader
                eyebrow="Execution Details"
                title="Structure the trade clearly"
                description="Good journals make trades measurable. The goal is reliable post-trade analysis, not fast but sloppy input."
              />
              <div className="mt-6 grid gap-5 md:grid-cols-2">
                <SelectField label="Asset Type" value={form.assetType} onChange={(e) => setForm({ ...form, assetType: e.target.value })} disabled={instrument.lockAssetType} hint={instrumentHint}>
                  {ASSET_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </SelectField>
                <Field label="Asset" value={form.asset} onChange={(e) => setForm({ ...form, asset: e.target.value })} placeholder="EURUSD, BTCUSD, NAS100, AAPL" />
                <SelectField label="Trade Currency" value={form.tradeCurrency} onChange={(e) => setForm({ ...form, tradeCurrency: e.target.value })} disabled={instrument.lockTradeCurrency} hint={instrumentHint}>
                  {CURRENCY_OPTIONS.map((currency) => (
                    <option key={currency} value={currency}>{currency}</option>
                  ))}
                </SelectField>
                <SelectField label="Direction" value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })} hint={directionHelper}>
                  <option value="long">Long</option>
                  <option value="short">Short</option>
                </SelectField>
                <Field label="Entry Price" type="number" value={form.entry_price} onChange={(e) => setForm({ ...form, entry_price: e.target.value })} placeholder="0.00" inputClassName="[appearance:textfield]" />
                <Field label="Exit Price" type="number" value={form.exit_price} onChange={(e) => setForm({ ...form, exit_price: e.target.value })} placeholder="0.00" inputClassName="[appearance:textfield]" />
                {usesLotSize ? (
                  <Field label="Lot Size" type="number" value={form.lot_size} onChange={(e) => setForm({ ...form, lot_size: e.target.value })} placeholder="0.10" min={0.01} step="any" inputClassName="[appearance:textfield]" hint={positionHint} />
                ) : (
                  <Field label="Quantity" type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="100" min={1} step="any" inputClassName="[appearance:textfield]" hint={positionHint} />
                )}
                <Field label="Confidence Score" type="number" value={form.confidenceScore} onChange={(e) => setForm({ ...form, confidenceScore: e.target.value })} min={1} max={10} placeholder="1 - 10" inputClassName="[appearance:textfield]" />
              </div>
              <div className="mt-5 rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                <div className="flex items-center gap-3">
                  <ArrowRightLeft className="h-5 w-5 text-sky-600 dark:text-sky-300" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">Execution reminder</p>
                    <p className="mt-1 text-xs leading-6 text-slate-500 dark:text-slate-400">
                      If the setup would not make sense to a mentor reviewing it later, the journal entry is not complete yet.
                    </p>
                  </div>
                </div>
              </div>
            </SurfaceCard>

            <div className="space-y-6">
              <SurfaceCard>
                <SectionHeader eyebrow="Timing" title="Anchor the execution window" description="Time context drives holding-period analysis and session-based review." />
                <div className="mt-6 grid gap-5">
                  <Field label="Opened At" type="datetime-local" value={form.opened_at} onChange={(e) => setForm({ ...form, opened_at: e.target.value })} />
                  <Field label="Closed At" type="datetime-local" value={form.closed_at} onChange={(e) => setForm({ ...form, closed_at: e.target.value })} />
                  <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                    <div className="flex items-center gap-3">
                      <Clock3 className="h-5 w-5 text-amber-600 dark:text-amber-300" />
                      <div>
                        <p className="text-sm font-semibold text-slate-900 dark:text-white">Holding time quality</p>
                        <p className="mt-1 text-xs leading-6 text-slate-500 dark:text-slate-400">
                          Accurate timestamps improve hold-time distributions and discipline analysis in analytics.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </SurfaceCard>

              <SurfaceCard>
                <SectionHeader eyebrow="Trade Review" title="Explain the setup and the lesson" description="This is the layer that turns a logbook into an edge-building system." />
                <div className="mt-6 grid gap-5 md:grid-cols-2">
                  <Field label="Market Condition" value={form.marketCondition} onChange={(e) => setForm({ ...form, marketCondition: e.target.value })} placeholder="Trending, ranging, news-driven" />
                  <Field label="Emotional State" value={form.emotionalState} onChange={(e) => setForm({ ...form, emotionalState: e.target.value })} placeholder="Calm, impatient, confident" />
                  <SelectField label="Execution Quality" value={form.executionQuality} onChange={(e) => setForm({ ...form, executionQuality: e.target.value })}>
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="average">Average</option>
                    <option value="poor">Poor</option>
                  </SelectField>
                  <SelectField label="Rule Adherence" value={form.ruleAdherence} onChange={(e) => setForm({ ...form, ruleAdherence: e.target.value })}>
                    <option value="followed">Followed</option>
                    <option value="partial">Partial</option>
                    <option value="violated">Violated</option>
                  </SelectField>
                  <Field label="Setup Tags" value={form.setupTagsText} onChange={(e) => setForm({ ...form, setupTagsText: e.target.value })} placeholder="breakout, london-open, trend-continuation" className="md:col-span-2" />
                  <Field label="Mistake Tags" value={form.mistakeTagsText} onChange={(e) => setForm({ ...form, mistakeTagsText: e.target.value })} placeholder="late-entry, over-sized, revenge-trade" className="md:col-span-2" />
                </div>
                <div className="mt-5 space-y-5">
                  <TextareaField label="Setup Description" value={form.setupDescription} onChange={(e) => setForm({ ...form, setupDescription: e.target.value })} placeholder="What exactly was the setup and why did it make sense?" rows={4} />
                  <TextareaField label="Execution Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="What did you do well or badly during entry and management?" rows={4} />
                  <TextareaField label="Lessons Learned" value={form.lessonsLearned} onChange={(e) => setForm({ ...form, lessonsLearned: e.target.value })} placeholder="What should you repeat or avoid next time?" rows={4} />
                </div>
              </SurfaceCard>
            </div>
          </div>

          <SurfaceCard className="mt-6">
            <SectionHeader eyebrow="Screenshots" title="Attach chart evidence" description="Include marked-up screenshots so your review captures the actual market structure, not only the story you remember later." />
            <div className="mt-6 space-y-4">
              <label className="flex cursor-pointer flex-col items-center justify-center rounded-[28px] border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center dark:border-gray-700 dark:bg-gray-900">
                <ImagePlus className="h-8 w-8 text-slate-500 dark:text-slate-300" />
                <span className="mt-3 text-sm font-semibold text-slate-900 dark:text-white">Upload screenshots</span>
                <span className="mt-2 text-xs leading-6 text-slate-500 dark:text-slate-400">PNG, JPG, GIF or supporting files up to 10MB each.</span>
                <input type="file" multiple accept="image/*" className="hidden" onChange={handleFileSelection} />
              </label>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {existingScreenshots.map((shot) => (
                  <div key={shot.url} className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                    <img src={shot.url} alt={shot.name || "Trade screenshot"} className="h-40 w-full rounded-2xl object-cover" />
                    <div className="mt-3 flex items-center justify-between gap-3">
                      <p className="truncate text-sm font-medium text-slate-700 dark:text-slate-200">{shot.name || "Screenshot"}</p>
                      <Button type="button" variant="secondary" className="px-3 py-2 text-xs" onClick={() => removeExistingScreenshot(shot.url)}>Remove</Button>
                    </div>
                  </div>
                ))}

                {selectedFiles.map((file) => (
                  <div key={file.name + file.lastModified} className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                    <div className="h-40 overflow-hidden rounded-2xl bg-black/5 dark:bg-white/5">
                      <img src={URL.createObjectURL(file)} alt={file.name} className="h-full w-full object-cover" />
                    </div>
                    <p className="mt-3 truncate text-sm font-medium text-slate-700 dark:text-slate-200">{file.name}</p>
                  </div>
                ))}
              </div>

              {!existingScreenshots.length && !selectedFiles.length ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">No screenshots attached yet.</p>
              ) : null}
            </div>
          </SurfaceCard>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <StatusPill tone="brand"><Tags className="mr-2 h-3.5 w-3.5" />{parseTagText(form.setupTagsText).length} setup tags</StatusPill>
              <StatusPill tone={form.ruleAdherence === "followed" ? "success" : form.ruleAdherence === "partial" ? "warning" : "danger"}>{form.ruleAdherence}</StatusPill>
              <StatusPill tone="neutral">Confidence {form.confidenceScore || "-"}/10</StatusPill>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {editTrade ? <Button type="button" variant="secondary" onClick={clearEdit}>Cancel</Button> : null}
              <Button type="submit" variant="primary" disabled={isSaving}>
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChartCandlestick className="h-4 w-4" />}
                {isSaving ? "Saving Trade..." : editTrade ? "Update Trade" : "Save Trade"}
              </Button>
            </div>
          </div>
        </fieldset>
      </form>
    </div>
  );
}
